import { paths } from 'src/generated/v1';
import { useQuery } from '@tanstack/react-query';
import API_BASE_URL from 'src/constants';
import { $api } from './api';
import { getToken } from './authToken';

interface AvgCalcWithProgressKpi {
  key?: string;
  title: string;
  value: number;
  progress: string;
  progress_color: string;
  kpi_type: 'avg_calc_with_progress';
  time_span: string;
}

interface AvgCalcKpi {
  title: string;
  value: number;
  kpi_type: 'avg_calc';
}
interface AvgCalcWithTimeSpanKpi {
  title: string;
  value: number;
  time_span?: string;
  kpi_type: 'avg_calc_with_time_span';
}

interface KeyValueListKpi {
  title: string;
  value: Record<string, number>;
  kpi_type: 'key_value_list';
}
interface ListKpi {
  title: string;
  value: string[];
  kpi_type: 'list';
}

interface DestinationWithPercentage {
  country: string;
  percentage: number;
}

interface TotalDestinationCountries {
  total_destination_countries: number;
}

export interface KeyValueListWithDiagramKpi {
  kpi_type: 'key_value_list_with_diagram';
  title: string;
  value: (DestinationWithPercentage | TotalDestinationCountries)[];
}

export interface DashboardKeyValueRankingKpi {
  kpi_type: 'dashboard_key_value_ranking';
  title: string;
  value: {
    product_name: string;
    product_id?: number;
    total_sold: number;
    available_quantity: number;
    image_url: string;
  }[];
}

interface TimeSeriesDataPoint {
  date: string;
  total_sales: number;
}

interface TimeSeriesMultiLine {
  last_30_days: TimeSeriesDataPoint[];
  last_60_days: TimeSeriesDataPoint[];
}

export interface DashboardTimeSeriesDiagramKpi {
  kpi_type: 'dashboard_time_series_diagram';
  title: string;
  totals: number;
  yLabels: string[];
  progress?: string | null;
  progress_color?: string | null;
  lines: {
    label?: string | null;
    color?: string | null;
    data: (number | null)[];
  }[];
  data: TimeSeriesDataPoint[] | TimeSeriesMultiLine;
}

export interface DashboardKeyValueListKpi {
  kpi_type: 'dashboard_key_value_list';
  title: string;
  value: {
    state: string;
    total_count: number;
  }[];
}

export interface DashboardAvgCalcWithProgressKpi {
  kpi_type: 'dashboard_avg_calc_with_progress';
  title: string;
  value: number;
  progress: string;
  progress_color: string;
}

export interface DashboardCircleProgressDiagramKpi {
  kpi_type: 'dashboard_circle_progress_diagram';
  title: string;
  data: number;
  totals: number;
}

export interface TopKpis {
  kpi_type: 'top_kpis';
  data: AvgCalcWithProgressKpi[];
}

export interface DashboardValueWithoutProgressKpi {
  kpi_type: 'dashboard_value_without_progress';
  title: string;
  value: number;
}

export interface DashboardBarChartDiagramKpi {
  kpi_type: 'dashboard_bar_chart_diagram';
  title: string;
  yLabels: string[];
  lines: {
    key?: string;
    label: string;
    data: number[];
    color: string;
  }[];
  bucket_dates?: string[];
  bucket?: 'day' | 'week' | 'month';
}

export interface ProductAffinityPair {
  product_a: string;
  product_b: string;
  orders: number;
  share: number;
}

export interface StatisticsProductAffinityKpi {
  kpi_type: 'statistics_product_affinity';
  title: string;
  value: ProductAffinityPair[];
}

export type DashboardKpi =
  | KeyValueListWithDiagramKpi
  | DashboardKeyValueRankingKpi
  | DashboardTimeSeriesDiagramKpi
  | DashboardKeyValueListKpi
  | DashboardAvgCalcWithProgressKpi
  | DashboardCircleProgressDiagramKpi
  | DashboardBarChartDiagramKpi
  | TopKpis
  | StatisticsProductAffinityKpi
  | DashboardValueWithoutProgressKpi;

export type Kpi = AvgCalcWithProgressKpi | AvgCalcKpi | AvgCalcWithTimeSpanKpi | KeyValueListKpi | ListKpi;

export type KpiModel = paths['/kpi/{res_model}']['get']['parameters']['path']['res_model'];
export interface KpiDateRange {
  date_from?: string;
  date_to?: string;
}
export const useKpiQuery = (model: KpiModel, dateRange?: KpiDateRange) => {
  return $api.useQuery('get', '/kpi/{res_model}', {
    params: {
      path: {
        res_model: model,
      },
      query: dateRange,
    },
  });
};

export type DashboardKpiModel = paths['/kpi/dashboard/{res_model}']['get']['parameters']['path']['res_model'];
export const useDashboardKpiQuery = (model: DashboardKpiModel, dateFrom?: string, dateTo?: string) => {
  return $api.useQuery('get', '/kpi/dashboard/{res_model}', {
    params: {
      path: {
        res_model: model,
      },
      query: { date_from: dateFrom, date_to: dateTo },
    } as unknown as { path: { res_model: DashboardKpiModel } },
  });
};

export const useStatisticsKpiQuery = (dateFrom?: string, dateTo?: string) => {
  return $api.useQuery('get', '/kpi/dashboard/{res_model}', {
    params: {
      path: {
        res_model: 'statistics.dashboard' as unknown as DashboardKpiModel,
      },
      query: { date_from: dateFrom, date_to: dateTo },
    } as unknown as { path: { res_model: DashboardKpiModel } },
  });
};

export type StockHistoryMode = 'total' | 'lots';

export interface StockHistoryKpi {
  kpi_type: 'dashboard_stacked_area_diagram';
  title: string;
  product_id: number;
  product_name: string;
  mode: StockHistoryMode;
  has_lots: boolean;
  bucket: 'day' | 'week' | 'month';
  y_labels: string[];
  bucket_dates: string[];
  series: { label: string; data: number[]; color: string }[];
}

// The /kpi/stock-history endpoint is not part of the generated OpenAPI types
// yet, so it is called via plain fetch + Bearer token — the same pattern the
// other not-yet-generated endpoints in services/inventory.ts use.
export const useStockHistoryQuery = (
  productId: number | null,
  mode: StockHistoryMode,
  dateFrom?: string,
  dateTo?: string,
  topN = 8,
) => {
  return useQuery<StockHistoryKpi>({
    queryKey: ['stock-history', productId, mode, dateFrom, dateTo, topN],
    enabled: productId != null,
    queryFn: async () => {
      const query = new URLSearchParams({ mode, top_n: String(topN) });
      if (dateFrom) query.set('date_from', dateFrom);
      if (dateTo) query.set('date_to', dateTo);
      const response = await fetch(`${API_BASE_URL}portal/kpi/stock-history/${productId}?${query.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch stock history (${response.status})`);
      }
      return response.json();
    },
  });
};
